import React, { ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBusiness } from '../context/BusinessContext';
import { BOOKING_STATUS } from '../constants/checkout';
import picklecourtsLogo from '../assets/picklecourts-logo.png';
import { AudienceStatus } from '../api/client';

interface Props {
  children: ReactNode;
  venueSummary?: AudienceStatus['venue'] | null;
}

// Navigation icons
const NAV_ICONS = {
  dashboard: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="9" />
      <rect x="14" y="3" width="7" height="5" />
      <rect x="14" y="12" width="7" height="9" />
      <rect x="3" y="16" width="7" height="5" />
    </svg>
  ),
  courts: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="3" />
      <path d="M12 3v6M12 15v6M3 12h6M15 12h6" />
    </svg>
  ),
  facility: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    </svg>
  ),
  integrations: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
    </svg>
  ),
  team: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
    </svg>
  ),
  bookings: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" />
      <rect x="9" y="3" width="6" height="4" rx="1" />
      <path d="M9 14l2 2 4-4" />
    </svg>
  ),
  venues: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  ),
  promos: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 12v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-7" />
      <path d="M2 7h20v5H2z" />
      <path d="M12 22V7" />
      <path d="M12 7H7.5a2.5 2.5 0 1 1 0-5C11 2 12 7 12 7z" />
      <path d="M12 7h4.5a2.5 2.5 0 1 0 0-5C13 2 12 7 12 7z" />
    </svg>
  ),
  audiences: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="9" cy="8" r="3" />
      <path d="M3 20v-1a6 6 0 0112 0v1" />
      <path d="M16 4a3 3 0 010 6M21 20v-1a6 6 0 00-3.4-5.4" />
    </svg>
  ),
  members: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="9" cy="7" r="4" />
      <path d="M3 21v-2a6 6 0 0112 0v2" />
      <path d="M16 11h6M19 8v6" />
    </svg>
  ),
  manualQueue: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 11l3 3L22 4" />
      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    </svg>
  ),
  payments: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <path d="M2 10h20" />
    </svg>
  ),
  goLive: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 00-2.91-.09z" />
      <path d="M12 15l-3-3a22 22 0 012-3.95A12.88 12.88 0 0122 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 01-4 2z" />
      <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
      <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
    </svg>
  ),
  logout: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" />
    </svg>
  ),
};

export default function DashboardLayout({ children, venueSummary = null }: Props) {
  const { user, logout, isSuperAdmin } = useAuth();
  const { business, reservations } = useBusiness();
  const navigate = useNavigate();

  const pendingBookingCount = reservations.filter(
    r => r.status === BOOKING_STATUS.PENDING_APPROVAL || r.status === 'pending' || r.status === 'manual_booking_approval'
  ).length;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="dashboard-layout">
      <aside className="sidebar-v2">
        {/* Logo Header */}
        <div className="sidebar-v2__header">
          <div className="sidebar-v2__logo">
            <div className="sidebar-v2__logo-icon">
              <img src={picklecourtsLogo} alt="PickleCourts" className="sidebar-v2__logo-image" />
            </div>
            <div className="sidebar-v2__logo-text">
              <span className="sidebar-v2__logo-name">PickleCourts</span>
              <span className="sidebar-v2__logo-tagline">Venue Portal</span>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="sidebar-v2__nav">
          <div className="sidebar-v2__nav-section">
            <span className="sidebar-v2__nav-label">Menu</span>
            <NavLink
              to="/dashboard"
              aria-label="Schedule"
              className={({ isActive }) => `sidebar-v2__nav-item ${isActive ? 'sidebar-v2__nav-item--active' : ''}`}
            >
              <span className="sidebar-v2__nav-icon">{NAV_ICONS.dashboard}</span>
              <span className="sidebar-v2__nav-text">Schedule</span>
            </NavLink>
            <NavLink
              to="/bookings"
              aria-label="Bookings"
              className={({ isActive }) => `sidebar-v2__nav-item ${isActive ? 'sidebar-v2__nav-item--active' : ''}`}
            >
              <span className="sidebar-v2__nav-icon">{NAV_ICONS.bookings}</span>
              <span className="sidebar-v2__nav-text">Bookings</span>
              {pendingBookingCount > 0 && (
                <span style={{
                  background: '#ef4444', color: '#fff', fontSize: '10px', fontWeight: 700,
                  minWidth: '18px', height: '18px', borderRadius: '9px', display: 'inline-flex',
                  alignItems: 'center', justifyContent: 'center', padding: '0 5px',
                  marginLeft: 'auto', lineHeight: 1,
                }}>{pendingBookingCount}</span>
              )}
            </NavLink>
            <NavLink
              to="/courts-pricing"
              aria-label="Courts and pricing"
              className={({ isActive }) => `sidebar-v2__nav-item ${isActive ? 'sidebar-v2__nav-item--active' : ''}`}
            >
              <span className="sidebar-v2__nav-icon">{NAV_ICONS.courts}</span>
              <span className="sidebar-v2__nav-text">Courts & Pricing</span>
            </NavLink>
            <NavLink
              to="/facility-setup"
              aria-label="Facility information"
              className={({ isActive }) => `sidebar-v2__nav-item ${isActive ? 'sidebar-v2__nav-item--active' : ''}`}
            >
              <span className="sidebar-v2__nav-icon">{NAV_ICONS.facility}</span>
              <span className="sidebar-v2__nav-text">Facility Info</span>
            </NavLink>
            <NavLink
              to="/members"
              aria-label="Members"
              className={({ isActive }) => `sidebar-v2__nav-item ${isActive ? 'sidebar-v2__nav-item--active' : ''}`}
            >
              <span className="sidebar-v2__nav-icon">{NAV_ICONS.members}</span>
              <span className="sidebar-v2__nav-text">Members</span>
            </NavLink>
            <NavLink
              to="/go-live"
              aria-label="Billing"
              className={({ isActive }) => `sidebar-v2__nav-item ${isActive ? 'sidebar-v2__nav-item--active' : ''}`}
            >
              <span className="sidebar-v2__nav-icon">{NAV_ICONS.goLive}</span>
              <span className="sidebar-v2__nav-text">Billing</span>
            </NavLink>
            <button
              type="button"
              className="sidebar-v2__nav-item sidebar-v2__mobile-logout"
              onClick={handleLogout}
              aria-label="Sign out"
              title="Sign out"
            >
              <span className="sidebar-v2__nav-icon">{NAV_ICONS.logout}</span>
            </button>
          </div>

          {isSuperAdmin && (
            <div className="sidebar-v2__nav-section">
              <span className="sidebar-v2__nav-label">Admin</span>
              <NavLink
                to="/superadmin"
                end
                className={({ isActive }) => `sidebar-v2__nav-item ${isActive ? 'sidebar-v2__nav-item--active' : ''}`}
              >
                <span className="sidebar-v2__nav-icon">{NAV_ICONS.venues}</span>
                <span className="sidebar-v2__nav-text">All Venues</span>
              </NavLink>
            </div>
          )}
        </nav>

        {/* Footer */}
        <div className="sidebar-v2__footer">
          {/* Venue Status Card */}
          {(business || venueSummary) && (
            <div className={`sidebar-v2__venue-card ${(business?.isPublished ?? venueSummary?.isPublished) ? 'sidebar-v2__venue-card--published' : 'sidebar-v2__venue-card--draft'}`}>
              <div className="sidebar-v2__venue-status">
                <span className="sidebar-v2__venue-dot" />
                <span className="sidebar-v2__venue-label">{(business?.isPublished ?? venueSummary?.isPublished) ? 'Live' : 'Draft'}</span>
              </div>
              <div className="sidebar-v2__venue-name">{business?.businessName || venueSummary?.name}</div>
              {(business?.location?.city || venueSummary?.city) && (
                <div className="sidebar-v2__venue-location">
                  {business?.location?.city || venueSummary?.city}, {business?.location?.state || venueSummary?.regionState}
                </div>
              )}
            </div>
          )}

          {/* User Profile */}
          <div className="sidebar-v2__user">
            <div className="sidebar-v2__user-avatar">
              {business ? getInitials(business.businessName) : (user ? getInitials(user.name) : '??')}
            </div>
            <div className="sidebar-v2__user-info">
              <div className="sidebar-v2__user-name">{user?.name}</div>
              <div className="sidebar-v2__user-role">
                {isSuperAdmin ? 'Super Admin' : 'Admin'}
              </div>
            </div>
            <button className="sidebar-v2__logout-btn" onClick={handleLogout} title="Sign out" aria-label="Sign out">
              {NAV_ICONS.logout}
            </button>
          </div>
        </div>
      </aside>

      <main className="main-content">{children}</main>
    </div>
  );
}
